import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// Icons
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MessageIcon from '@mui/icons-material/Message';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import BiotechIcon from '@mui/icons-material/Biotech';
import SecurityIcon from '@mui/icons-material/Security';
import LanguageIcon from '@mui/icons-material/Language';

const features = [
  {
    title: 'Video Consultations',
    description: 'Connect with healthcare providers through secure, high-quality video calls from the comfort of your home.',
    icon: <VideocamIcon sx={{ fontSize: 40 }} />,
    color: '#1DB4A3' // Logo teal
  },
  {
    title: 'Appointment Scheduling',
    description: 'Book appointments with specialists at times that work for your schedule, with instant confirmation.',
    icon: <CalendarMonthIcon sx={{ fontSize: 40 }} />,
    color: '#E53935' // Logo red
  },
  {
    title: 'Secure Messaging',
    description: 'Communicate with your healthcare team through encrypted messaging for follow-ups and quick questions.',
    icon: <MessageIcon sx={{ fontSize: 40 }} />,
    color: '#0A4A6E' // Deep navy
  },
  {
    title: 'Pharmacy Integration',
    description: 'Get prescriptions sent directly to your preferred pharmacy for convenient pickup or delivery.',
    icon: <LocalPharmacyIcon sx={{ fontSize: 40 }} />,
    color: '#1DB4A3' // Logo teal
  },
  {
    title: 'Lab & Radiology',
    description: 'Access and share lab results and imaging studies securely with your healthcare providers.',
    icon: <BiotechIcon sx={{ fontSize: 40 }} />,
    color: '#E53935' // Logo red
  },
  {
    title: 'HIPAA Compliant',
    description: 'Your health information is protected with enterprise-grade security and encryption.',
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    color: '#0A4A6E' // Deep navy
  }
];

const testimonials = [
  {
    name: "Dr. Sarah Johnson",
    role: "Family Physician",
    image: "/images/maledr_looking_left.png",
    quote: "Alpha01 has transformed how I provide care to my patients. The platform is intuitive and allows me to maintain the personal connection that is so important in healthcare."
  },
  {
    name: "Michael Rodriguez",
    role: "Patient",
    image: "",
    quote: "As someone with a chronic condition, Alpha01 has made managing my healthcare so much easier. I can connect with my doctor without taking time off work for appointments."
  },
  {
    name: "Dr. Amara Patel",
    role: "Pediatrician",
    image: "",
    quote: "The platform's security features give me confidence when discussing sensitive health information, and the integration with labs makes reviewing results seamless."
  }
];

const howItWorks = [
  {
    step: 1,
    title: 'Create Your Account',
    description: 'Sign up in minutes with a simple, secure registration process.',
    color: '#1DB4A3' // Logo teal
  },
  {
    step: 2,
    title: 'Find Your Provider',
    description: 'Browse specialists or connect with your existing healthcare team.',
    color: '#E53935' // Logo red
  },
  {
    step: 3,
    title: 'Schedule a Consultation',
    description: 'Book an appointment at a time that works for you.',
    color: '#0A4A6E' // Deep navy
  },
  {
    step: 4,
    title: 'Receive Care',
    description: 'Connect via video for your consultation and follow-up as needed.',
    color: '#1DB4A3' // Logo teal
  }
];

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [animate, setAnimate] = useState(false);
  
  useEffect(() => {
    setAnimate(true);
  }, []);

  return (
    <Box sx={{ bgcolor: '#f5f7fa', width: '100vw', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <Box
        role="banner"
        aria-label="Alpha01 Telemedicine Hero Section"
        sx={{
          bgcolor: '#0A4A6E', 
          color: 'white',
          pt: { xs: 10, md: 15 },
          pb: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          width: '100vw',
          minHeight: { md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url(/images/male_dr.png)',
          backgroundSize: { xs: 'contain', md: 'cover' },
          backgroundPosition: { xs: 'top center', md: 'right center' },
          filter: 'brightness(1.1) contrast(1.1)',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(10, 74, 110, 0.7), rgba(10, 74, 110, 0.5))',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="md" sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 4, md: 8 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: { xs: 400, md: 500 }, position: 'relative' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: { xs: 400, md: 420 },
              position: 'relative',
              zIndex: 2,
              gap: { xs: 0, md: 6 },
            }}
          >
            {/* Left: Title, subtext, buttons */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
                justifyContent: 'center',
                textAlign: { xs: 'center', md: 'left' },
                minWidth: 0,
                mb: { xs: 4, md: 0 },
              }}
            >
              <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: 'primary.dark', mb: { xs: 1.5, md: 2 }, fontSize: { xs: '2.6rem', sm: '3rem', md: '3.5rem' }, textShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                Healthcare at Your Fingertips
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'white', maxWidth: 500, mb: { xs: 3, md: 4 }, fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 700 }}>
                Connect with healthcare providers from anywhere through secure video consultations, messaging, and integrated care.
              </Typography>
              <Fade in={animate} timeout={1000}>
                <Box sx={{ display: 'flex', gap: { xs: 3, md: 2 }, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' }, mt: { xs: 1, md: 0 } }}>
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <Button variant="contained" color="error" size="large" sx={{ fontWeight: 700, borderRadius: 3, px: 4, py: 1.5, boxShadow: '0 4px 16px rgba(229,57,53,0.12)', fontSize: { xs: '1rem', md: '1.1rem' }, transition: 'all 0.2s cubic-bezier(.4,2,.6,1)', '&:hover': { transform: 'scale(1.05)', boxShadow: '0 8px 32px rgba(229,57,53,0.18)' }, '&:active': { transform: 'scale(0.98)' }, '&:focus': { outline: '3px solid #1DB4A3', outlineOffset: '2px' } }}>
                      Get Started
                    </Button>
                  </Link>
                  <Link to="/login" style={{ textDecoration: 'none' }}>
                    <Button variant="outlined" color="success" size="large" sx={{ fontWeight: 700, borderRadius: 3, px: 4, py: 1.5, fontSize: { xs: '1rem', md: '1.1rem' }, borderWidth: 2, borderColor: 'success.main', color: 'success.main', backgroundColor: 'white', '&:hover': { backgroundColor: 'rgba(29,180,163,0.08)', borderColor: 'success.main' }, '&:focus': { outline: '3px solid #1DB4A3', outlineOffset: '2px' } }}>
                      Sign In
                    </Button>
                  </Link>
                </Box>
              </Fade>
            </Box>
            {/* Right: Logo */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-end' },
                width: '100%',
                minWidth: 0,
              }}
            >
              <Link to="/" style={{ display: 'block', width: 'fit-content', margin: '0 auto' }} aria-label="Go to Home">
                <Box
                  component="img"
                  src="/images/logo.svg"
                  alt="Alpha01 Medical Consultants Logo - Telemedicine Platform"
                  aria-label="Alpha01 Medical Consultants Logo"
                  sx={{
                    height: { xs: 160, sm: 180, md: 220 },
                    maxWidth: { xs: 320, sm: 340, md: 360 },
                    borderRadius: 4,
                    boxShadow: '0 8px 32px 0 rgba(0,0,0,0.10)',
                    backgroundColor: 'rgba(255,255,255,0.98)',
                    p: 2,
                    objectFit: 'contain',
                    mx: 'auto',
                    display: 'block',
                  }}
                />
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* Need to Sponsor a Patient Card */}
      <Container maxWidth="md" sx={{ my: 4, display: 'flex', justifyContent: 'center', mt: { xs: 8, sm: 8, md: 4 }, px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, maxWidth: 540, width: '100%', textAlign: 'center', background: 'linear-gradient(135deg, #E3F8F6 0%, #F5F7FA 100%)', boxShadow: '0 4px 24px 0 rgba(29,180,163,0.08)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 1, fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
            Need to Sponsor a Patient?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, fontSize: { xs: '0.95rem', md: '1rem' } }}>
            Are you living abroad and want to support the healthcare of a loved one back home? With Alpha01, you can sponsor telehealth for a family member in Zambia (or other countries), manage payments, and help them access quality care. Consent-based, secure, and easy to get started.
          </Typography>
          <Button variant="contained" color="primary" size="large" component={Link} to="/register" sx={{ fontWeight: 700, borderRadius: 3, px: { xs: 3, md: 4 }, py: { xs: 1.25, md: 1.5 }, fontSize: { xs: '0.95rem', md: '1rem' }, minHeight: 44 }}>
            Learn How Sponsorship Works
          </Button>
        </Paper>
      </Container>
      {/* Trusted By Section */}
      <Container maxWidth="lg" sx={{ py: 6, mx: 'auto', px: { xs: 1, sm: 2, md: 0 }, width: '100%', maxWidth: '100%' }}>
        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            mb: 4,
            color: '#0A4A6E',
            fontWeight: 500
          }}
        >
          TRUSTED BY HEALTHCARE PROVIDERS AND PATIENTS
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {['Hospital A', 'Clinic B', 'Medical Center C', 'Healthcare Group D'].map((partner, index) => (
            <Grid item key={index} xs={6} sm={3}>
              <Box 
                sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 60,
                  opacity: 0.7
                }}
              >
                <Typography variant="h6" sx={{ color: '#0A4A6E', fontWeight: 600 }}>
                  {partner}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
      {/* Features Section */}
      <Box sx={{
        bgcolor: 'white',
        py: { xs: 8, md: 12 },
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative background shapes */}
        <Box sx={{
          position: 'absolute',
          top: -80,
          left: -120,
          width: 300,
          height: 300,
          borderRadius: '50%',
          bgcolor: '#1DB4A3', // Logo teal
          opacity: 0.08,
          zIndex: 0,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -100,
          right: -100,
          width: 350,
          height: 350,
          borderRadius: '50%',
          bgcolor: '#E53935', // Logo red
          opacity: 0.07,
          zIndex: 0,
        }} />
        <Container maxWidth="lg" sx={{ mx: 'auto', position: 'relative', zIndex: 1, px: { xs: 1, sm: 2, md: 0 }, width: '100%', maxWidth: '100%' }}>
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ 
              mb: 2,
              color: '#0A4A6E',
              fontWeight: 700,
              letterSpacing: '-1px',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Comprehensive Telemedicine Features
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              mb: { xs: 6, md: 8 },
              color: '#666',
              maxWidth: 800,
              mx: 'auto',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Our platform combines cutting-edge technology with intuitive design to deliver a seamless healthcare experience.
          </Typography>
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center" sx={{ maxWidth: 1200, mx: 'auto' }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} display="flex" justifyContent="center">
                <Fade in={animate} timeout={1000} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      width: '100%',
                      maxWidth: 370,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 4,
                      background: 'rgba(245, 247, 250, 0.85)',
                      boxShadow: '0 2px 16px 0 rgba(10, 74, 110, 0.07)',
                      border: '1px solid #e3e8ee',
                      transition: 'transform 0.3s cubic-bezier(.4,2,.6,1), box-shadow 0.3s cubic-bezier(.4,2,.6,1)',
                      '&:hover': {
                        transform: 'translateY(-10px) scale(1.03)',
                        boxShadow: '0 8px 32px 0 rgba(10, 74, 110, 0.13)',
                        borderColor: feature.color,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                      <Box 
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: `${feature.color}15`,
                          color: feature.color,
                          mb: 3,
                          mx: 'auto',
                          boxShadow: '0 2px 8px 0 rgba(10, 74, 110, 0.08)',
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        align="center"
                        sx={{ 
                          mb: 2,
                          fontWeight: 700,
                          color: '#0A4A6E',
                          letterSpacing: '-0.5px',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        align="center"
                        sx={{ color: '#666', fontSize: '1.08rem', fontWeight: 400 }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      {/* How It Works Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: { xs: 8, md: 12 }, width: '100vw' }}>
        <Container maxWidth="lg" sx={{ mx: 'auto', px: { xs: 1, sm: 2, md: 0 }, width: '100%', maxWidth: '100%' }}>
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ 
              mb: 2,
              color: '#0A4A6E',
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            How It Works
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              mb: { xs: 6, md: 8 },
              color: '#666',
              maxWidth: 800,
              mx: 'auto',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Getting started with Alpha01 is simple and straightforward.
          </Typography>
          
          <Box sx={{ position: 'relative', maxWidth: 1000, mx: 'auto', mt: 6 }}>
            {/* Connection line */}
            {!isMobile && (
              <Box 
                sx={{ 
                  position: 'absolute',
                  top: 40, // Position at the center of the circles
                  left: '5%',
                  right: '5%',
                  height: 4,
                  bgcolor: '#1DB4A3', // Logo teal
                  zIndex: 0
                }} 
              />
            )}
            
            <Grid 
              container 
              sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: { xs: 3, md: 4 },
                alignItems: 'start',
                '& > .MuiGrid-item': {
                  width: '100%',
                  padding: 0
                }
              }}
            >
              {howItWorks.map((step, index) => (
                <Grid item key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Fade in={animate} timeout={1000} style={{ transitionDelay: `${index * 150}ms` }}>
                    <Box sx={{ 
                      position: 'relative', 
                      zIndex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                      textAlign: 'center'
                    }}>
                      <Box 
                        sx={{ 
                          width: { xs: 70, md: 80 },
                          height: { xs: 70, md: 80 },
                          borderRadius: '50%',
                          bgcolor: step.color,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: { xs: 2, md: 3 },
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                          fontWeight: 700,
                          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {step.step}
                      </Box>
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        sx={{ 
                          mb: { xs: 1.5, md: 2 },
                          fontWeight: 600,
                          color: '#0A4A6E',
                          minHeight: { xs: 50, md: 60 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#666',
                          minHeight: { xs: 60, md: 80 },
                          width: '100%',
                          fontSize: { xs: '0.95rem', md: '1rem' },
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
      {/* Testimonials Section */}
      <Box sx={{ bgcolor: 'white', py: { xs: 8, md: 12 }, width: '100vw' }}>
        <Container maxWidth="lg" sx={{ mx: 'auto', px: { xs: 1, sm: 2, md: 0 }, width: '100%', maxWidth: '100%' }}>
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ 
              mb: 2,
              color: '#0A4A6E',
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            What Our Users Say
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              mb: { xs: 6, md: 8 },
              color: '#666',
              maxWidth: 800,
              mx: 'auto',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Hear from healthcare providers and patients who use Alpha01.
          </Typography>
          
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in={animate} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 4,
                      p: 3,
                      bgcolor: '#f5f7fa',
                      position: 'relative'
                    }}
                  >
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: 20,
                        left: 20,
                        fontSize: '4rem',
                        color: '#1DB4A3', // Logo teal
                        opacity: 0.3,
                        lineHeight: 1
                      }}
                    >
                      "
                    </Box>
                    <CardContent sx={{ flexGrow: 1, pt: 4 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mb: 4,
                          fontStyle: 'italic',
                          color: '#444',
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        "{testimonial.quote}"
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          sx={{ 
                            width: 60, 
                            height: 60,
                            mr: 2,
                            bgcolor: index % 2 === 0 ? '#1DB4A3' : '#E53935' // Logo teal or Logo red
                          }}
                        >
                          {testimonial.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600,
                              color: '#0A4A6E'
                            }}
                          >
                            {testimonial.name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ color: '#666' }}
                          >
                            {testimonial.role}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      {/* CTA Section */}
      <Box 
        sx={{ 
          bgcolor: '#0A4A6E', 
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="md" sx={{ mx: 'auto', px: { xs: 1, sm: 2, md: 0 }, width: '100%', maxWidth: '100%' }}>
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
              }}
            >
              Ready to Transform Your Healthcare Experience?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 4, md: 5 },
                fontWeight: 400,
                opacity: 0.9,
                maxWidth: 800,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' },
              }}
            >
              Join thousands of patients and providers who are already benefiting from our telemedicine platform.
            </Typography>
            <Button 
              component={Link} 
              to="/register" 
              variant="contained" 
              size="large"
              sx={{ 
                bgcolor: '#E53935', // Logo red
                '&:hover': {
                  bgcolor: '#C62828', // Darker red
                },
                px: { xs: 4, md: 6 },
                py: { xs: 1.5, md: 2 },
                borderRadius: 2,
                fontWeight: 600,
                fontSize: { xs: '1rem', md: '1.1rem' },
                minHeight: 44,
              }}
            >
              Get Started Today
            </Button>
          </Box>
        </Container>
        
        {/* Decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: '#1DB4A3', // Logo teal
            opacity: 0.2,
            zIndex: 0
          }} 
        />
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            bgcolor: '#E53935', // Logo red
            opacity: 0.2,
            zIndex: 0
          }} 
        />
      </Box>
      {/* Footer */}
      <Box sx={{ bgcolor: '#0A4A6E', color: 'white', py: { xs: 4, md: 6 }, borderTop: '1px solid rgba(255,255,255,0.1)', width: '100vw' }}>
        <Container maxWidth="lg" sx={{ mx: 'auto', px: { xs: 1, sm: 2, md: 0 }, width: '100%', maxWidth: '100%' }}>
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center" alignItems="flex-start">
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Alpha01 Telemedicine
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, maxWidth: 250 }}>
                Providing intelligent healthcare delivery through technology, connecting patients with providers seamlessly.
              </Typography>
              <Link to="/" style={{ display: 'block', width: 'fit-content', marginTop: 16 }} aria-label="Go to Home">
                <Box 
                  component="img"
                  src="/images/logo.svg"
                  alt="Alpha01 Medical Consultants Logo - Telemedicine Platform"
                  aria-label="Alpha01 Medical Consultants Logo"
                  sx={{ 
                    maxWidth: 120,
                    height: 'auto',
                    mt: 2,
                    opacity: 0.9,
                    borderRadius: 2,
                    padding: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  }}
                />
              </Link>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Platform
              </Typography>
              <Typography variant="body2" component={Link} to="/features" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Features
              </Typography>
              <Typography variant="body2" component={Link} to="/how-it-works" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                How It Works
              </Typography>
              <Typography variant="body2" component={Link} to="/security" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Security
              </Typography>
              <Typography variant="body2" component={Link} to="/integrations" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Integrations
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Company
              </Typography>
              <Typography variant="body2" component={Link} to="/about" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                About Us
              </Typography>
              <Typography variant="body2" component={Link} to="/external-contact" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Contact
              </Typography>
              <Typography variant="body2" component={Link} to="/careers" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Careers
              </Typography>
              <Typography variant="body2" component={Link} to="/blog" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Blog
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Resources
              </Typography>
              <Typography variant="body2" component={Link} to="/help" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Help Center
              </Typography>
              <Typography variant="body2" component={Link} to="/providers" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                For Providers
              </Typography>
              <Typography variant="body2" component={Link} to="/patients" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                For Patients
              </Typography>
              <Typography variant="body2" component={Link} to="/partners" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Partners
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Legal
              </Typography>
              <Typography variant="body2" component={Link} to="/terms" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Terms of Service
              </Typography>
              <Typography variant="body2" component={Link} to="/privacy" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Privacy Policy
              </Typography>
              <Typography variant="body2" component={Link} to="/hipaa" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                HIPAA Compliance
              </Typography>
              <Typography variant="body2" component={Link} to="/accessibility" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Accessibility
              </Typography>
            </Grid>
            
            {/* Added telemedicine image to balance footer layout */}
            <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
              <Box 
                component="img"
                src="/images/male_dr.png"
                alt="Telemedicine consultation"
                sx={{ 
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: 180,
                  borderRadius: 2,
                  opacity: 0.8,
                  filter: 'brightness(1.2)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="body2" align="center" sx={{ opacity: 0.8 }}>
            © {new Date().getFullYear()} Alpha01 Medical Consultants. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
